import productMatrixConfig from "@/config/productMatrix.json";
import type { SimCardDetail } from "@/services/model";
import {
  GetSimListDevicesItem,
  SimCustomerInfoCreditClass,
} from "@/services/model";
import type { PrepareBasketRequestDevice } from "@/services/model/prepareBasketRequestDevice";
import { shouldFetchCockpitRoamingForSim } from "@/utils/cockpitRoamingFetch";
import { mobileAbos, ServiceIDs } from "@/utils/constants";

export type ProductCategory = "mobile" | "home" | "data";

type CategoryConfig = {
  deviceId: number;
  deviceDescription: string;
  deviceEnum: PrepareBasketRequestDevice;
};

export type ProductSpeed = {
  download?: number;
  upload?: number;
  fullspeed?: boolean;
};

export type InternationalCallsSec = "notIncluded" | "unlimited" | `${number}`;

type ProductConfig = {
  category: ProductCategory;
  defaultAliasKey: string;
  download?: number;
  upload?: number;
  fullspeed?: boolean;
  internationalCallsSec?: InternationalCallsSec;
  /** Included EU/USA roaming data in marketed GB (Flat Mobile One/Two/Three). */
  roamingDataGb?: number;
  /** Backend roamingProduct id expected for this Flat Mobile tariff's included package. */
  roamingProduct?: string;
  attributes?: Record<string, unknown>;
};

export type ProductDevice = {
  id: number;
  description: string;
  enum: PrepareBasketRequestDevice;
};

const categories = productMatrixConfig.categories as Record<
  ProductCategory,
  CategoryConfig
>;

const products = productMatrixConfig.products as Record<string, ProductConfig>;

const categoryDeviceEnumMap: Record<ProductCategory, GetSimListDevicesItem> = {
  mobile: GetSimListDevicesItem.Smartphone,
  home: GetSimListDevicesItem.Router,
  data: GetSimListDevicesItem.IoT_Device,
};

const isFlatHomeProductId = (productId: number) =>
  productId === ServiceIDs.FlatHome4G || productId === ServiceIDs.FlatHome5G;

export const getProductCategory = (productId?: number): ProductCategory => {
  if (!productId) {
    return "mobile";
  }

  const explicit = products[String(productId)]?.category;
  if (explicit) {
    return explicit;
  }

  if (mobileAbos.includes(String(productId))) {
    return "mobile";
  }

  if (isFlatHomeProductId(productId)) {
    return "home";
  }

  return "data";
};

export const getDeviceForProduct = (productId?: number): ProductDevice => {
  const category = getProductCategory(productId);
  const config = categories[category];

  return {
    id: config.deviceId,
    description: config.deviceDescription,
    enum: config.deviceEnum,
  };
};

export const getDeviceEnumForProduct = (
  productId?: number,
): GetSimListDevicesItem =>
  categoryDeviceEnumMap[getProductCategory(productId)];

export const getDefaultAliasKey = (productId?: number): string =>
  (productId ? products[String(productId)]?.defaultAliasKey : undefined) ?? "";

export const getProductAttributes = (
  productId?: number,
): Record<string, unknown> | undefined =>
  productId ? products[String(productId)]?.attributes : undefined;

export const getProductSpeed = (
  productId?: number,
): ProductSpeed | undefined => {
  if (!productId) {
    return undefined;
  }

  const product = products[String(productId)];
  if (!product) {
    return undefined;
  }

  const { download, upload, fullspeed } = product;
  if (download === undefined && upload === undefined && !fullspeed) {
    return undefined;
  }

  return { download, upload, fullspeed };
};

export const getInternationalCallsSec = (
  productId?: number,
): InternationalCallsSec | undefined =>
  productId ? products[String(productId)]?.internationalCallsSec : undefined;

/** Marketed included roaming data volume in GB for Flat Mobile products. */
export const getRoamingDataGb = (productId?: number): number | undefined =>
  productId ? products[String(productId)]?.roamingDataGb : undefined;

/**
 * Backend `roamingProduct` id expected for the included Flat Mobile roaming
 * package (e.g. "mvno.1082" for Flat Mobile One).
 */
export const getRoamingProduct = (productId?: number): string | undefined =>
  productId ? products[String(productId)]?.roamingProduct : undefined;

export type RoamingCallPackageUsageSkipReason =
  | "creditClassC"
  | "notIncluded"
  | "unlimited";

export type RoamingCallPackageUsageDecision =
  | { shouldFetch: false; reason: RoamingCallPackageUsageSkipReason }
  | { shouldFetch: true; allowanceSec: number };

export type InternationalCallsTileDisplay =
  | { kind: "hidden" }
  | { kind: "notAllowed" }
  | { kind: "unlimited" }
  | { kind: "usage"; allowanceSec: number };

const productIncludesInternationalCalls = (
  internationalCallsSec: InternationalCallsSec | undefined,
): internationalCallsSec is Exclude<InternationalCallsSec, "notIncluded"> =>
  !!internationalCallsSec && internationalCallsSec !== "notIncluded";

/**
 * Tile display for "Anrufe EU/USA".
 * 1. Credit class C → still show the row as unavailable when the product includes calls
 * 2. Else use productMatrix `internationalCallsSec`:
 *    - notIncluded → hide
 *    - unlimited (Flat Mobile Three) → unlimited
 *    - finite seconds → usage bar (API-backed)
 */
export const getInternationalCallsTileDisplay = ({
  productId,
  creditClass,
}: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): InternationalCallsTileDisplay => {
  const internationalCallsSec = getInternationalCallsSec(productId);

  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return productIncludesInternationalCalls(internationalCallsSec)
      ? { kind: "notAllowed" }
      : { kind: "hidden" };
  }

  if (!productIncludesInternationalCalls(internationalCallsSec)) {
    return { kind: "hidden" };
  }

  if (internationalCallsSec === "unlimited") {
    return { kind: "unlimited" };
  }

  return {
    kind: "usage",
    allowanceSec: Number(internationalCallsSec),
  };
};

/**
 * Decides whether `/sim/roamingCallPackageUsage` is needed.
 * Skip when: credit class C, product has no included minutes, or allowance is unlimited.
 * Fetch only for finite included allowances (e.g. One 1800 / Two 6000 sec).
 */
export const getRoamingCallPackageUsageDecision = ({
  productId,
  creditClass,
}: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): RoamingCallPackageUsageDecision => {
  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return { shouldFetch: false, reason: "creditClassC" };
  }

  const display = getInternationalCallsTileDisplay({ productId, creditClass });

  if (display.kind === "hidden") {
    return { shouldFetch: false, reason: "notIncluded" };
  }

  if (display.kind === "unlimited") {
    return { shouldFetch: false, reason: "unlimited" };
  }

  if (display.kind === "notAllowed") {
    return { shouldFetch: false, reason: "creditClassC" };
  }

  return {
    shouldFetch: true,
    allowanceSec: display.allowanceSec,
  };
};

export const shouldFetchRoamingCallPackageUsage = (params: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): boolean => getRoamingCallPackageUsageDecision(params).shouldFetch;

/** SIM IDs that need a roaming-call usage fetch for cockpit tiles. */
export const getSimIdsNeedingRoamingCallPackageUsage = (
  sims: SimCardDetail[],
  creditClass?: SimCustomerInfoCreditClass | string | null,
  needsIdCheck?: boolean,
): number[] => {
  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return [];
  }

  return sims
    .filter(
      (sim) =>
        shouldFetchCockpitRoamingForSim(sim, needsIdCheck) &&
        shouldFetchRoamingCallPackageUsage({
          productId: getProductIdFromSim(sim),
          creditClass,
        }),
    )
    .map((sim) => sim.simId)
    .filter((simId): simId is number => simId !== undefined);
};

export const getProductIdFromSim = (sim?: SimCardDetail): number | undefined =>
  sim?.serviceID ?? sim?.successorForRenew?.id;

export const getDeviceLabelKeyForProduct = (
  productId?: number,
): GetSimListDevicesItem => getDeviceEnumForProduct(productId);

export const getDeviceLabelKeyForSim = (
  sim?: SimCardDetail,
): GetSimListDevicesItem | undefined => {
  const productId = getProductIdFromSim(sim);

  return productId ? getDeviceLabelKeyForProduct(productId) : undefined;
};
